import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type Immutable from 'immutable';
import classNames from 'classnames';

import type { CommentablePersonProfile, CreationSourceEnum } from 'js/src/client';
import AddComment from 'js/src/components/common/comments/AddComment';
import type { onBeforeCommentingT } from 'js/src/components/common/comments/CommentActionsPlugin';
import VideoComments from 'js/src/components/common/comments/VideoComment';
import {
    getCommentIdsBySceneId,
    getCommentsAreLoading,
    getOpenCommentIdsBySceneId,
    getResolvedCommentIdsBySceneId,
    resolveReopenComments,
} from 'js/src/data/comments';
import { getLanguageName, getVideoTranslationBySceneId } from 'js/src/data/translations';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

const VIEW_OPEN_COMMENTS = 'OPEN';
const VIEW_RESOLVED_COMMENTS = 'RESOLVED';

type CommentThreadTabHeadersProps = {
    viewMode: string;
    setViewMode: (viewMode: string) => void;
    resolvedCommentCount: number;
};

const CommentThreadTabHeaders = React.memo(function CommentThreadTabHeaders({
    viewMode,
    setViewMode,
    resolvedCommentCount,
}: CommentThreadTabHeadersProps) {
    return (
        <div className="video-comment-list__header">
            <div
                className={classNames('video-comment-list__header-item', {
                    active: viewMode === VIEW_OPEN_COMMENTS,
                })}
                onClick={() => setViewMode(VIEW_OPEN_COMMENTS)}
            >
                Open
            </div>
            <div
                className={classNames('video-comment-list__header-item', {
                    active: viewMode === VIEW_RESOLVED_COMMENTS,
                })}
                onClick={() => setViewMode(VIEW_RESOLVED_COMMENTS)}
            >
                {`Resolved (${resolvedCommentCount})`}
            </div>
        </div>
    );
});

type VideoCommentListProps = {
    slideId: string;
    video_id: number | null;
    onBeforeCommenting?: onBeforeCommentingT;
    showCommentButtons?: boolean;
    mentionableUsers: Immutable.List<ImmutableMap<CommentablePersonProfile>>;
    videoOwner: number | null;
    isPopover?: boolean;
    onCommentInputFocus?: VoidFunction;
    className?: string;
    videoCreationSource: CreationSourceEnum | null;
    parentVideoLanguage?: string | null;
};

/**
 * This component renders a list of comments from the Redux store
 * If there are no comments, it'll render just <AddComment /> for the user to leave a comment
 *
 * This component also automatically scrolls to the bottom when a new comment is added
 */
const VideoCommentList = React.memo(function VideoCommentList({
    slideId,
    video_id,
    onBeforeCommenting,
    className,
    showCommentButtons = false,
    mentionableUsers,
    videoOwner,
    isPopover = false,
    onCommentInputFocus,
    videoCreationSource,
    parentVideoLanguage,
}: VideoCommentListProps) {
    const dispatch = useAppDispatch();
    const videoCommentIds = useSelector(getCommentIdsBySceneId(slideId));
    const openCommentIds = useSelector(getOpenCommentIdsBySceneId(slideId));
    const resolvedCommentIds = useSelector(getResolvedCommentIdsBySceneId(slideId));
    const videoTranslations = useSelector(getVideoTranslationBySceneId(slideId));
    const parentVideoLanguageName = useSelector((state: RootState) =>
        parentVideoLanguage ? getLanguageName(state, parentVideoLanguage) : undefined,
    );
    const resolvedCommentCount = resolvedCommentIds.size;
    const hasComments = videoCommentIds.size > 0;
    const commentsAreLoading = useSelector(getCommentsAreLoading);
    const [viewMode, setViewMode] = React.useState(VIEW_OPEN_COMMENTS);
    const [persistTabHeaders, setPersistTabHeaders] = React.useState(false);

    useEffect(() => {
        // in creator, when comments are resolved, we want to keep tab headers visible until user closes
        // the comment popover. if headers disappear while popover is still open, the UI is jumpy because
        // combination of multiple comments disappearing and tab headers also disappearing
        if (resolvedCommentCount > 0) {
            setPersistTabHeaders(true);
        }

        if (!isPopover && resolvedCommentCount === 0) {
            // hide headers immediately if there are 0 resolved comments
            // in creator, because it's a popover, we want to keep headers
            // until user closes the popover, which triggers re-render when
            // comment popover re-opens
            setPersistTabHeaders(false);
        }
    }, [resolvedCommentCount, isPopover]);

    const resolveThreadCallback = () => {
        dispatch(
            resolveReopenComments({
                scene_id: slideId,
                resolve_comments: true,
                video_id: video_id,
            }),
        );
    };
    const reopenThreadCallback = (data?: { comment_id: number }) => {
        if (!data) throw new Error('comment_id is required to reopen comment');
        // if re-opening last comment, flip the viewMode to open comment thread
        if (resolvedCommentCount <= 1) {
            setViewMode(VIEW_OPEN_COMMENTS);
        }
        const { comment_id } = data;
        dispatch(
            resolveReopenComments({
                comment_id,
                scene_id: slideId,
                resolve_comments: false,
                video_id: video_id,
            }),
        );
    };

    useEffect(() => {
        if (!commentsAreLoading && hasComments) {
            window.analytics.track('Viewed Comment from page', {
                slide_id: slideId,
                video_id,
                user_id: SERVER.USER && SERVER.USER.id,
                comment_id: videoCommentIds.map((id) => `${id}`).toJS(),
                page_url: window.location.href,
                is_lumen5_user: !!SERVER.USER,
            });
        }
    }, [commentsAreLoading, hasComments, slideId, videoCommentIds, video_id]);

    const resolvedCommentsNode =
        resolvedCommentCount > 0 ? (
            <VideoComments
                threadType={viewMode}
                threadAction={reopenThreadCallback}
                videoCommentIds={resolvedCommentIds}
                videoCreationSource={videoCreationSource}
            />
        ) : (
            <div className="video-comment-list__empty-resolved">
                <p>There are no resolved comments.</p>
            </div>
        );

    return (
        <div className={classNames(className, 'video-comment-list')}>
            {viewMode === VIEW_OPEN_COMMENTS && (
                <AddComment
                    slideId={slideId}
                    video_id={video_id}
                    mentionableUsers={mentionableUsers}
                    videoOwner={videoOwner}
                    showCommentButtons={showCommentButtons || !hasComments}
                    onBeforeCommenting={onBeforeCommenting}
                    onCommentInputFocus={onCommentInputFocus}
                />
            )}

            {persistTabHeaders && (
                <CommentThreadTabHeaders
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    resolvedCommentCount={resolvedCommentCount}
                />
            )}

            {/* VIEWING OPEN COMMENTS  */}
            {viewMode === VIEW_OPEN_COMMENTS && (
                <VideoComments
                    threadType={viewMode}
                    threadAction={resolveThreadCallback}
                    videoCommentIds={openCommentIds}
                    videoCreationSource={videoCreationSource}
                    videoOriginalText={videoTranslations}
                    parentVideoLanguageName={parentVideoLanguageName}
                />
            )}

            {/* VIEWING RESOLVED COMMENTS  */}
            {viewMode === VIEW_RESOLVED_COMMENTS && resolvedCommentsNode}
        </div>
    );
});

export default VideoCommentList;
